/**
 * Home Page - Visual-First Marketplace
 *
 * Landing page with immersive hero, visual listing cards, and engaging CTAs
 */

import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { listings, Listing } from "@/lib/mockData";
import { useSearchStore } from "@/lib/store";

const stats = [
  { label: "Active listings", value: "1,280", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
  { label: "Verified brokers", value: "86", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
  { label: "Avg. response", value: "3h", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
  { label: "Countries", value: "12", icon: "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
];

const boatTypes = [
  { id: "powerboat", label: "Power Boats", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
  { id: "sailboat", label: "Sailboats", icon: "M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" },
  { id: "yacht", label: "Yachts", icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" },
  { id: "catamaran", label: "Catamarans", icon: "M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" },
];

// Hero boat images
const heroImages = [
  "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1920&q=80",
  "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&q=80",
  "https://images.unsplash.com/photo-1559494007-9f5847c49d94?w=1920&q=80",
];

// Listing card images for visual display
const listingImages: Record<string, string> = {
  "lagoon-42": "https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?w=800&q=80",
  "axopar-37": "https://images.unsplash.com/photo-1605281317010-fe5ffe798166?w=800&q=80",
  "oceanis-38": "https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=800&q=80",
};

// Visual listing card for homepage
function VisualListingCard({ listing }: { listing: Listing }) {
  const imageUrl = listingImages[listing.id] || "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=800&q=80";

  const tierColors = {
    Featured: "bg-gradient-to-r from-blue-500 to-cyan-500",
    Top: "bg-gradient-to-r from-amber-500 to-orange-500",
    Base: "bg-slate-600",
  };

  return (
    <Link to={`/listing/${listing.id}`} className="block group">
      <div className="card hover:shadow-2xl transition-all duration-300 overflow-hidden h-full">
        {/* Hero Image */}
        <div className="relative h-52 overflow-hidden">
          <img
            src={imageUrl}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Tier Badge */}
          <div className={`absolute top-4 left-4 ${tierColors[listing.tier]} text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg`}>
            {listing.tier}
          </div>

          {/* Save Button */}
          <button
            className="absolute top-4 right-4 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white hover:scale-110 transition-all opacity-0 group-hover:opacity-100"
            onClick={(e) => { e.preventDefault(); }}
          >
            <svg className="w-4 h-4 text-slate-600 hover:text-red-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>

          {/* Price & Location Overlay */}
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-end justify-between">
              <div className="text-white">
                <p className="text-sm opacity-80 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  {listing.location}
                </p>
              </div>
              <div className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg">
                <p className="text-lg font-bold text-[var(--ink)]">{listing.price}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="text-lg font-bold text-[var(--ink)] group-hover:text-[var(--accent)] transition-colors mb-2">
            {listing.title}
          </h3>

          {/* Quick Specs */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium text-slate-600">
              {listing.length}
            </span>
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium text-slate-600">
              {listing.year}
            </span>
          </div>

          <p className="text-slate-600 text-sm line-clamp-2">{listing.summary}</p>

          {/* View Link */}
          <div className="mt-4 pt-3 border-t border-[var(--ash)]/50">
            <span className="text-[var(--accent)] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
              View Details
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { setSearch, setType, setLocation } = useSearchStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [currentHeroImage] = useState(0);

  const topListings = listings.filter((l) => l.tier === "Top" || l.tier === "Featured");
  const recentListings = listings.slice(0, 6);

  const handleSearch = () => {
    if (searchQuery) setSearch(searchQuery);
    if (selectedType) setType([selectedType]);
    if (locationQuery) setLocation(locationQuery);
    navigate("/search");
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Boat361 Marketplace",
    description: "Boat and yacht marketplace with verified listings and concierge-led support.",
    url: "https://boat361.com/",
    brand: { "@type": "Brand", name: "Boat361" },
  };

  return (
    <div className="min-h-screen bg-[var(--mist)]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={heroImages[currentHeroImage]}
            alt="Luxury yacht on the ocean"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex items-center">
          <div className="max-w-2xl">
            <p className="text-[var(--accent)] font-semibold text-sm uppercase tracking-widest mb-4">
              Premium Boat Marketplace
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Find Your Perfect
              <span className="block text-[var(--accent)]">Vessel</span>
            </h1>
            <p className="text-white/80 text-lg md:text-xl mb-8 max-w-lg">
              Discover verified boats from trusted brokers. From sleek powerboats to luxury yachts, your next adventure starts here.
            </p>

            {/* Search Box */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-2xl">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by make, model, or keyword..."
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-sm"
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-sm min-w-[140px]"
                >
                  <option value="">All Types</option>
                  {boatTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                  placeholder="Location"
                  className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-sm min-w-[140px]"
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  className="px-8 py-3 bg-[var(--ink)] text-white rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search
                </button>
              </div>
            </div>

            {/* Quick Type Buttons */}
            <div className="flex flex-wrap gap-2 mt-6">
              {boatTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => { setType([type.id]); navigate("/search"); }}
                  className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full hover:bg-white hover:text-[var(--ink)] transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={type.icon} />
                  </svg>
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-[var(--ash)]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <div key={stat.label} className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[var(--accent)]/10 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-[var(--accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={stat.icon} />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[var(--ink)]">{stat.value}</p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 py-12 space-y-16">
        {/* Featured Listings */}
        {topListings.length > 0 && (
          <section>
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-[var(--accent)] text-sm font-semibold uppercase tracking-wide mb-2">
                  Handpicked for you
                </p>
                <h2 className="text-3xl font-bold text-[var(--ink)]">Featured Listings</h2>
              </div>
              <Link
                to="/search"
                className="hidden sm:flex items-center gap-2 text-[var(--accent)] font-semibold hover:gap-3 transition-all"
              >
                View all listings
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {topListings.map((listing) => (
                <VisualListingCard key={listing.id} listing={listing} />
              ))}
            </div>
            <div className="sm:hidden mt-6 text-center">
              <Link
                to="/search"
                className="inline-flex items-center gap-2 text-[var(--accent)] font-semibold"
              >
                View all listings
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </section>
        )}

        {/* Recent Listings */}
        <section>
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-slate-500 text-sm font-semibold uppercase tracking-wide mb-2">
                Fresh on the market
              </p>
              <h2 className="text-3xl font-bold text-[var(--ink)]">Recent Listings</h2>
            </div>
            <Link
              to="/search"
              className="hidden sm:flex items-center gap-2 text-[var(--accent)] font-semibold hover:gap-3 transition-all"
            >
              Browse all
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentListings.map((listing) => (
              <VisualListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative overflow-hidden rounded-3xl">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=1920&q=80"
              alt="Yacht on ocean"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)]/90 to-[var(--ink)]/70" />
          </div>
          <div className="relative z-10 px-8 py-16 md:py-20 md:px-16">
            <div className="max-w-xl">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to sell your boat?
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Join our network of verified brokers and reach thousands of qualified buyers. Get started in minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/dashboard/listings/new"
                  className="px-8 py-4 bg-white text-[var(--ink)] rounded-xl font-semibold hover:bg-slate-100 transition-all shadow-lg text-center"
                >
                  Create a Listing
                </Link>
                <Link
                  to="/dashboard"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border border-white/30 rounded-xl font-semibold hover:bg-white/20 transition-all text-center"
                >
                  Broker Dashboard
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="text-center py-8">
          <p className="text-slate-500 text-sm mb-6">Trusted by leading brokers across Europe</p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-50 grayscale">
            {["Fraser Yachts", "Burgess", "Northrop & Johnson", "Y.CO", "Edmiston"].map((broker) => (
              <span key={broker} className="text-xl font-bold text-slate-400">{broker}</span>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
